import React, { useState, useEffect } from 'react'
import { Modal, Button, Form } from 'semantic-ui-react'
import styled from 'styled-components'
import { X } from 'phosphor-react'
import { FlexRowBetween } from 'components'
import { useImpersonation } from 'context'
import { useNotification } from 'hooks/toast'
import { ImpersonationErrorCode } from 'utility'

const MIN_REASON_LENGTH = 3

const Sub = styled.p`
    margin: 0 0 16px 0;
    color: #6b7280;
    font-size: 13px;
`

const Guardrails = styled.div`
    background: #fef3c7;
    border-left: 3px solid #f59e0b;
    padding: 10px 12px;
    font-size: 12px;
    color: #78350f;
    margin: 14px 0;
    border-radius: 4px;
    ul {
        margin: 6px 0 0 18px;
        padding: 0;
    }
`

const InlineError = styled.div`
    color: #dc2626;
    font-size: 12px;
    margin-top: 6px;
`

// Screen 2 of the handoff: reason-required confirm modal for "Login as this user".
export const LoginAsUserModal = ({ open, onClose, target }) => {
    const { startImpersonation, starting } = useImpersonation()
    const { errorToast, warningToast } = useNotification()
    const [reason, setReason] = useState('')
    const [touched, setTouched] = useState(false)

    useEffect(() => {
        if (open) {
            setReason('')
            setTouched(false)
        }
    }, [open])

    const trimmedReason = reason.trim()
    const isReasonValid = trimmedReason.length >= MIN_REASON_LENGTH

    const handleError = errorCode => {
        switch (errorCode) {
            case ImpersonationErrorCode.ReasonRequired:
                // Keep the modal open and surface the inline hint.
                setTouched(true)
                return
            case ImpersonationErrorCode.TargetNotFound:
            case ImpersonationErrorCode.TargetInactive:
                onClose()
                errorToast(
                    "This user can't be impersonated (inactive or not found).",
                )
                return
            case ImpersonationErrorCode.SelfImpersonation:
                onClose()
                errorToast("You can't impersonate yourself.")
                return
            case ImpersonationErrorCode.AlreadyImpersonating:
                onClose()
                warningToast(
                    'You have an active impersonation session. End it first.',
                )
                return
            case ImpersonationErrorCode.NotSuperAdmin:
                onClose()
                errorToast('You are not allowed to impersonate users.')
                return
            case ImpersonationErrorCode.KillSwitchOff:
                onClose()
                warningToast(
                    'Impersonation is disabled by ops. Contact your admin.',
                )
                return
            default:
                onClose()
                errorToast(
                    'Impersonation failed - please try again or contact devops.',
                )
        }
    }

    const handleStart = async () => {
        setTouched(true)
        if (!isReasonValid) return
        const result = await startImpersonation(target, trimmedReason)
        // On success the page hard-reloads as the target user, so nothing else
        // to do here. Only failures come back.
        if (result && !result.ok) {
            handleError(result.errorCode)
        }
    }

    return (
        <Modal open={open} onClose={onClose} size="tiny">
            <Modal.Header>
                <FlexRowBetween>
                    <span>
                        Login as <strong>{target?.email || target?.name}</strong>?
                    </span>
                    <X className="pointer" size={28} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Sub>
                    You&apos;ll browse the app with this user&apos;s permissions,
                    menus, and dashboards until you click &quot;Return to
                    admin&quot;.
                </Sub>
                <Form>
                    <Form.Field error={touched && !isReasonValid}>
                        <label>
                            Reason (required, min {MIN_REASON_LENGTH} characters)
                            - shown in the audit log
                        </label>
                        <textarea
                            rows={3}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            onBlur={() => setTouched(true)}
                            placeholder="e.g. support-ticket-1234 / reproducing 'menu not showing'"
                        />
                        {touched && !isReasonValid && (
                            <InlineError>
                                Reason is required (min {MIN_REASON_LENGTH}{' '}
                                chars).
                            </InlineError>
                        )}
                    </Form.Field>
                </Form>
                <Guardrails>
                    <strong>Guardrails while impersonating:</strong>
                    <ul>
                        <li>
                            Destructive endpoints (delete audit/user/customer,
                            change password, promote role, payment) are blocked.
                        </li>
                        <li>
                            Session auto-ends after 30 min hard cap OR 15 min
                            idle.
                        </li>
                        <li>
                            Every action is logged under this session; visible on
                            the Impersonation History page.
                        </li>
                    </ul>
                </Guardrails>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} disabled={starting}>
                    Cancel
                </Button>
                <Button
                    primary
                    onClick={handleStart}
                    disabled={!isReasonValid || starting}
                    loading={starting}
                >
                    Start impersonation
                </Button>
            </Modal.Actions>
        </Modal>
    )
}
